import { useEnvironmentRegistries } from '@/react/portainer/environments/queries/useEnvironmentRegistries';
import {
  Registry,
  RegistryTypes,
} from '@/react/portainer/registries/types/registry';
import { EnvironmentId } from '@/react/portainer/environments/types';

/**
 * useAddonRegistries lists every registry configured for the add-on environment,
 * sorted by name.
 *
 * Unfiltered by default: pulling an image needs no catalog, and Docker Hub is a
 * common home for a private one. Callers that search a registry's repositories
 * narrow the list with the listableRegistries select.
 */
export function useAddonRegistries(
  environmentId?: EnvironmentId,
  { select }: { select?: (registries: Array<Registry>) => Array<Registry> } = {}
) {
  return useEnvironmentRegistries(environmentId ?? 0, {
    hideDefault: true,
    enabled: !!environmentId,
    select: (registries) => {
      const sorted = [...registries].sort((a, b) =>
        a.Name.localeCompare(b.Name)
      );
      return select ? select(sorted) : sorted;
    },
  });
}

/**
 * listableRegistries keeps only the registries whose repositories can be
 * searched, so a chart can be picked from them. Docker Hub and Quay do not serve
 * a catalog — the same registries the Helm install flow leaves out.
 *
 * Declared once rather than inline at the call site: React Query re-runs select
 * on every render, and a stable reference keeps the result identity stable too.
 */
export function listableRegistries(
  registries: Array<Registry>
): Array<Registry> {
  return registries.filter(
    (registry) =>
      registry.Type !== RegistryTypes.DOCKERHUB &&
      registry.Type !== RegistryTypes.QUAY
  );
}
